import { Link, useRouterState } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import type { PermissionAction, PermissionSubject } from '@bliks-io/contracts';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Popover,
  PopoverTrigger,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  Badge
} from '@bliks-io/ui';
import { type AppIcon } from '@/constants/entityIcons';
import { BetaBadge } from '@/components/beta-badge/BetaBadge';
import { ComingSoonPopover } from './ComingSoonPopover';

export interface NavRequiredPermission {
  subject: PermissionSubject;
  action?: PermissionAction;
}

export interface NavSubItem {
  title: string;
  url: string;
  icon?: AppIcon;
  comingSoon?: boolean;
  beta?: boolean;
  requiredPermission?: NavRequiredPermission;
}

export interface NavMainItem {
  title: string;
  /** Groups whose visibility derives purely from children may omit a URL of their own. */
  url?: string;
  icon?: AppIcon;
  isActive?: boolean;
  comingSoon?: boolean;
  beta?: boolean;
  badge?: number;
  /** Keep rendering as a group dropdown even when only one sub-item is visible. */
  keepGroup?: boolean;
  requiredPermission?: NavRequiredPermission;
  items?: NavSubItem[];
}

// Two apps can carry the same route label, so the title alone is not unique.
function navItemKey(item: { title: string; url?: string }) {
  return `${item.url ?? ''} ${item.title}`;
}

interface NavMainProps {
  title?: string;
  items: NavMainItem[];
}

/** The red count on a sidebar icon; its parent positions it (`relative`). */
export function NavBadge({ count }: { count: string }) {
  return (
    <Badge
      variant="destructive"
      className="absolute -top-3 -right-3 scale-75 h-4 min-w-4 px-1 flex items-center justify-center"
    >
      {count}
    </Badge>
  );
}

// Component to render icon with badge
function NavIconWithBadge({
  Icon,
  isActive,
  badgeCount,
  isComingSoon
}: {
  Icon?: AppIcon;
  isActive: boolean;
  badgeCount?: string;
  isComingSoon?: boolean;
}) {
  let iconColor = 'text-fg-secondary';
  if (isActive) {
    iconColor = 'text-black';
  } else if (isComingSoon) {
    iconColor = 'text-fg-disabled';
  }

  return (
    <div className="relative">
      {Icon && <Icon className={`h-[21px] w-[21px] ${iconColor}`} strokeWidth={1.5} />}
      {badgeCount && <NavBadge count={badgeCount} />}
    </div>
  );
}

// Component for dropdown menu items with subitems
function DropdownNavItem({
  item,
  slug,
  isActive,
  badgeCount
}: {
  item: NavMainItem;
  slug: string;
  isActive: boolean;
  badgeCount?: string;
}) {
  const { t } = useTranslation();
  const activeIconColor = isActive ? 'text-black' : 'text-fg-secondary';

  return (
    <div>
      <DropdownMenu>
        <Tooltip>
          <TooltipTrigger asChild>
            <DropdownMenuTrigger asChild>
              <button
                className={`flex h-10 w-full items-center justify-center rounded-md transition-colors hover:bg-sidebar-accent ${
                  isActive ? 'bg-sidebar-accent font-medium' : ''
                } data-[state=open]:bg-sidebar-accent`}
              >
                <div className="relative">
                  {item.icon && (
                    <item.icon
                      className={`h-[21px] w-[21px] ${activeIconColor}`}
                      strokeWidth={1.5}
                    />
                  )}
                  {badgeCount && (
                    <Badge
                      variant="destructive"
                      className="absolute -top-3 -right-3 scale-75 h-4 min-w-4 px-1 flex items-center justify-center"
                    >
                      {badgeCount}
                    </Badge>
                  )}
                </div>
              </button>
            </DropdownMenuTrigger>
          </TooltipTrigger>
          <TooltipContent side="right" align="center">
            <span className="flex items-center gap-1.5">
              {t(item.title)}
              {item.beta && <BetaBadge />}
            </span>
          </TooltipContent>
        </Tooltip>
        <DropdownMenuContent side="right" align="start" className="rounded-lg min-w-56">
          {item.items?.map((subItem) => {
            const fullPath = `/${slug}${subItem.url}`;
            const SubIcon = subItem.icon;

            if (subItem.comingSoon) {
              return (
                <DropdownMenuItem key={navItemKey(subItem)} disabled className="opacity-100">
                  <div className="flex items-center gap-2 text-fg-subtle">
                    {SubIcon && <SubIcon className="h-4 w-4" strokeWidth={1.5} />}
                    {t(subItem.title)}
                  </div>
                </DropdownMenuItem>
              );
            }

            return (
              <DropdownMenuItem asChild key={navItemKey(subItem)}>
                <Link to={fullPath} className="cursor-pointer flex items-center gap-2">
                  {SubIcon && <SubIcon className="h-4 w-4" strokeWidth={1.5} />}
                  {t(subItem.title)}
                  {subItem.beta && <BetaBadge />}
                </Link>
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

// Component for coming soon items
function ComingSoonNavItem({
  item,
  isActive,
  badgeCount
}: {
  item: NavMainItem;
  isActive: boolean;
  badgeCount?: string;
}) {
  const { t } = useTranslation();

  return (
    <div>
      <Popover>
        <Tooltip>
          <PopoverTrigger asChild>
            <TooltipTrigger asChild>
              <button
                className={`flex h-10 w-full items-center justify-center rounded-md transition-colors hover:bg-sidebar-accent ${
                  isActive ? 'bg-sidebar-accent font-medium' : ''
                }`}
              >
                <NavIconWithBadge
                  Icon={item.icon}
                  isActive={isActive}
                  badgeCount={badgeCount}
                  isComingSoon
                />
              </button>
            </TooltipTrigger>
          </PopoverTrigger>
          <TooltipContent side="right" align="center">
            {t(item.title)}
          </TooltipContent>
        </Tooltip>
        <ComingSoonPopover route={item.url ?? ''} title={item.title} />
      </Popover>
    </div>
  );
}

// Component for regular link items
function RegularNavItem({
  item,
  slug,
  isActive,
  badgeCount
}: {
  item: NavMainItem;
  slug: string;
  isActive: boolean;
  badgeCount?: string;
}) {
  const { t } = useTranslation();
  const fullPath = `/${slug}${item.url}`;

  return (
    <div>
      <Tooltip>
        <TooltipTrigger asChild>
          <Link
            to={fullPath}
            className={`flex h-10 w-full items-center justify-center rounded-md transition-colors hover:bg-sidebar-accent ${
              isActive ? 'bg-sidebar-accent font-medium' : ''
            }`}
          >
            <NavIconWithBadge Icon={item.icon} isActive={isActive} badgeCount={badgeCount} />
          </Link>
        </TooltipTrigger>
        <TooltipContent side="right" align="center">
          <span className="flex items-center gap-1.5">
            {t(item.title)}
            {item.beta && <BetaBadge />}
          </span>
        </TooltipContent>
      </Tooltip>
    </div>
  );
}

// Helper component to render a nav item
function NavItemRenderer({
  item,
  slug,
  isActive,
  hasActiveSubItem,
  getBadgeCount
}: {
  item: NavMainItem;
  slug: string;
  isActive: (url: string) => boolean;
  hasActiveSubItem: (item: NavMainItem) => boolean;
  getBadgeCount: (badge?: number) => string | undefined;
}) {
  // Groups without a URL never match the route themselves; their highlight
  // comes from hasActiveSubItem.
  const itemActive = item.url ? isActive(item.url) : false;
  const hasSubItems = item.items && item.items.length > 0;
  const subItemActive = hasActiveSubItem(item);
  const badgeCount = getBadgeCount(item.badge);
  const combinedActive = itemActive || subItemActive;

  if (hasSubItems) {
    return (
      <DropdownNavItem item={item} slug={slug} isActive={combinedActive} badgeCount={badgeCount} />
    );
  }

  if (item.comingSoon) {
    return <ComingSoonNavItem item={item} isActive={itemActive} badgeCount={badgeCount} />;
  }

  return <RegularNavItem item={item} slug={slug} isActive={itemActive} badgeCount={badgeCount} />;
}

export function NavMain({ title, items }: NavMainProps) {
  const routerState = useRouterState();
  const currentPath = routerState.location.pathname;

  // Extract slug from current path
  const slug = currentPath.split('/')[1] || '';

  // Check if a nav item is active
  const isActive = (itemUrl: string): boolean => {
    // Remove slug from current path to get the relative path
    const pathWithoutSlug = currentPath.replace(`/${slug}`, '');

    // For the root path, check exact match
    if (itemUrl === '/') {
      return pathWithoutSlug === '/' || pathWithoutSlug === '';
    }

    // For other paths, check if current path starts with the item URL
    // and ensure it's a complete path segment (not a substring match)
    return pathWithoutSlug === itemUrl || pathWithoutSlug.startsWith(`${itemUrl}/`);
  };

  // Check if any sub-item is active
  const hasActiveSubItem = (item: NavMainItem): boolean => {
    if (!item.items) return false;
    return item.items.some((subItem) => isActive(subItem.url));
  };

  // Format badge count (9+ for values > 9)
  const getBadgeCount = (badge?: number): string | undefined => {
    if (!badge) return undefined;
    return badge > 9 ? '9+' : badge.toString();
  };

  return (
    <div className="p-2">
      {title && (
        <div className="px-2 py-1 text-xs font-medium sr-only text-sidebar-foreground/70">
          {title}
        </div>
      )}
      <div className="flex flex-col gap-3">
        {items.map((item) => (
          <NavItemRenderer
            key={navItemKey(item)}
            item={item}
            slug={slug}
            isActive={isActive}
            hasActiveSubItem={hasActiveSubItem}
            getBadgeCount={getBadgeCount}
          />
        ))}
      </div>
    </div>
  );
}
